import { truncateText } from '@chronosphereio/core';
import { merge } from 'lodash';
import { StateCreator } from 'zustand';
import { getPanelKeyFromRef, PanelDefinition } from '@/core';
import { EMPTY_PANEL_GROUP_TITLE, GRID_ITEM_HEIGHT } from '@/dashboards/constants';
import { getUniquePanelKey, getYForNewRow } from '@/dashboards/utils/panelUtils';
import { createPanelDefinition, generateId, Middleware } from './common';
import { DashboardStoreState } from './DashboardProvider';
import { HistorySlice } from './history-slice';
import {
  addPanelGroup,
  createEmptyPanelGroup,
  PanelGroupDefinition,
  PanelGroupId,
  PanelGroupItemId,
  PanelGroupItemLayout,
  PanelGroupSlice,
} from './panel-group-slice';
import { PanelSlice } from './panel-slice';

/**
 * Slice that handles the visual editor state and actions for adding or editing Panels.
 */
export interface PanelEditorSlice {
  /**
   * Initial values for add panel if default panel kind is defined
   */
  initialValues?: Pick<PanelEditorValues, 'panelDefinition'>;

  /**
   * State for the panel editor when its open, otherwise undefined when it's closed.
   */
  panelEditor?: PanelEditorState;

  /**
   * Opens the editor for editing an existing panel by providing its layout coordinates.
   */
  openEditPanel: (panelGroupItemId: PanelGroupItemId) => void;

  /**
   * Opens the editor for adding a new Panel to a panel group.
   */
  openAddPanel: (panelGroupId?: PanelGroupId) => void;

  /**
   * Edits panel definition
   */
  editPanelDefinition: (panelGroupItemId: PanelGroupItemId, panelDefinition: PanelDefinition) => void;

  /**
   * Duplicates a panel as a split panel.
   */
  duplicateAsSplitPanel: (panel: PanelDefinition) => void;
}

export interface PanelEditorState {
  /**
   * Whether we're adding a new panel, or editing an existing panel.
   */
  mode: 'Add' | 'Edit';

  /**
   * Key to distinguish panel from others.
   * Can be undefined in add panel drawer, but important in edit panel
   * to ensure queryKey is correct and cached queries are used.
   */
  panelKey?: string;

  /**
   * Initial values for the things that can be edited about a panel.
   */
  initialValues: PanelEditorValues;

  /**
   * Applies changes, but doesn't close the editor.
   */
  applyChanges: (next: PanelEditorValues) => void;

  /**
   * Close the editor.
   */
  close: () => void;
}

/**
 * Panel values that can be edited in the panel editor.
 */
export interface PanelEditorValues {
  groupId: PanelGroupId;
  panelDefinition: PanelDefinition;
}

/**
 * Curried function for creating the PanelEditorSlice.
 */
export function createPanelEditorSlice(): StateCreator<
  // Actions in here need to modify both Panels and Panel Groups state
  PanelEditorSlice & PanelSlice & PanelGroupSlice & DashboardStoreState & HistorySlice,
  Middleware,
  [],
  PanelEditorSlice
> {
  // Return the state creator function for Zustand that uses the panels provided as intitial state
  return (set, get) => ({
    panelEditor: undefined,

    openEditPanel(panelGroupItemId) {
      const { panels, panelGroups } = get();
      const panelGroup = panelGroups[panelGroupItemId.panelGroupId];
      if (panelGroup === undefined) {
        throw new Error(`Could not find panel group ${panelGroupItemId.panelGroupId}`);
      }

      // Figure out the panel key at that location
      const { panelGroupId, panelGroupItemLayoutId: panelGroupLayoutId } = panelGroupItemId;

      const panelKey = getPanelKey(panelGroupItemId, panelGroups);

      if (panelKey === undefined) {
        throw new Error(`Could not find panel group item ${panelGroupItemId}`);
      }

      // Find the panel to edit
      const panelToEdit = panels[panelKey];
      if (panelToEdit === undefined) {
        throw new Error(`Cannot find panel with key '${panelKey}'`);
      }

      // Normalize panel definition to ensure discard changes has correct defaults in case a
      // new option was added since the last time the panel was saved
      const defaultPanelDefinition = createPanelDefinition();
      const mergedPanelDefinitionToEdit = merge({}, defaultPanelDefinition, panelToEdit);

      const editorState: PanelEditorState = {
        mode: 'Edit',
        initialValues: {
          groupId: panelGroupItemId.panelGroupId,
          panelDefinition: mergedPanelDefinitionToEdit,
        },
        panelKey,
        applyChanges: (next) => {
          get().initializeHistoryIfEmpty();
          set(
            (state) => {
              state.panels[panelKey] = next.panelDefinition;

              // If the panel didn't change groups, nothing else to do
              if (next.groupId === panelGroupId) {
                return;
              }

              // Move panel to the new group
              const existingGroup = state.panelGroups[panelGroupId];
              if (existingGroup === undefined) {
                throw new Error(`Missing panel group ${panelGroupId}`);
              }

              // If the group is not a grid, we can't move items between groups
              if (existingGroup.kind !== 'Grid' || !panelGroupLayoutId) {
                throw new Error(`Panel group ${panelGroupId} is not a Grid panel group`);
              }

              const existingLayoutIdx = existingGroup.itemLayouts.findIndex(
                (layout) => layout.i === panelGroupLayoutId
              );
              const existingLayout = existingGroup.itemLayouts[existingLayoutIdx];
              const existingPanelKey = existingGroup.itemPanelKeys[panelGroupLayoutId];
              if (existingLayoutIdx === -1 || existingLayout === undefined || existingPanelKey === undefined) {
                throw new Error(`Missing panel group item ${panelGroupLayoutId}`);
              }

              // Remove item from the old group
              existingGroup.itemLayouts.splice(existingLayoutIdx, 1);
              delete existingGroup.itemPanelKeys[panelGroupLayoutId];

              // Add item to the end of the new group
              const newGroup = state.panelGroups[next.groupId];
              if (newGroup === undefined) {
                throw new Error(`Could not find new group ${next.groupId}`);
              } else if (newGroup.kind !== 'Grid') {
                throw new Error(`Panel group ${next.groupId} is not a Grid panel group`);
              }

              newGroup.itemLayouts.push({
                i: existingLayout.i,
                x: 0,
                y: getYForNewRow(newGroup),
                w: existingLayout.w,
                h: existingLayout.h,
              });
              newGroup.itemPanelKeys[existingLayout.i] = existingPanelKey;
            },
            false,
            '[Panel] applyChanges'
          );
          const displayName = next.panelDefinition.spec.display?.name || panelKey;
          get().pushToHistory({
            actionId: `edited-panel-${panelKey}-${generateId()}`, // Generate id so that undoing multiple edits to same panel works
            actionName: `Edited panel ${truncateText(displayName, 20)}`,
          });
        },
        close: () => {
          set(
            (state) => {
              state.panelEditor = undefined;
            },
            false,
            '[Panel] close'
          );
        },
      };

      // Open the editor with the new state
      set(
        (state) => {
          state.panelEditor = editorState;
        },
        false,
        '[Panel] openEditPanel'
      );
    },

    duplicateAsSplitPanel(panel: PanelDefinition) {
      const { panels, openEditPanelGroup } = get();
      const newGroup = createEmptyPanelGroup();
      newGroup.title = panel.spec.display.name;
      newGroup.kind = 'SplitPanel';
      const panelKey = getUniquePanelKey({ panels });
      const panelGroupId = newGroup.id;
      if (newGroup.kind === 'SplitPanel') {
        newGroup.content = {
          $ref: `#/spec/panels/${panelKey}`,
        };
      }
      set(
        (state) => {
          state.panels[panelKey] = panel;
          addPanelGroup(state, newGroup);
        },
        false,
        '[Panel] duplicateAsSplitPanel'
      );
      openEditPanelGroup(panelGroupId);
    },

    openAddPanel(panelGroupId) {
      // If a panel group isn't supplied, add to the first group or create a group if there aren't any
      let newGroup: PanelGroupDefinition | undefined = undefined;
      panelGroupId ??= get().panelGroupOrder[0];

      // Check if the first panel group is a Grid group, if not create a new one
      if (panelGroupId !== undefined) {
        const firstGroup = get().panelGroups[panelGroupId];
        if (firstGroup?.kind !== 'Grid') {
          newGroup = createEmptyPanelGroup();
          newGroup.title = EMPTY_PANEL_GROUP_TITLE;
          panelGroupId = newGroup.id;
        }
      } else {
        newGroup = createEmptyPanelGroup();
        newGroup.title = EMPTY_PANEL_GROUP_TITLE;
        panelGroupId = newGroup.id;
      }

      const editorState: PanelEditorState = {
        mode: 'Add',
        initialValues: {
          groupId: panelGroupId,
          panelDefinition: get().initialValues?.panelDefinition ?? createPanelDefinition(),
        },
        applyChanges: (next) => {
          const { panels } = get();

          const panelKey = getUniquePanelKey({ panels });

          set((state) => {
            // Add a panel
            state.panels[panelKey] = next.panelDefinition;

            // Also add a panel group item referencing the panel
            const group = state.panelGroups[next.groupId];
            if (group === undefined) {
              throw new Error(`Missing panel group ${next.groupId}`);
            } else if (group.kind !== 'Grid') {
              throw new Error(`Panel group ${next.groupId} is not a Grid panel group`);
            }
            const layout: PanelGroupItemLayout = {
              i: generateId().toString(),
              x: 0,
              y: getYForNewRow(group),
              w: 12,
              h: GRID_ITEM_HEIGHT,
            };
            group.itemLayouts.push(layout);
            group.itemPanelKeys[layout.i] = panelKey;
          });
        },
        close: () => {
          set(
            (state) => {
              state.panelEditor = undefined;
            },
            false,
            '[Panel] close'
          );
        },
      };

      set(
        (state) => {
          // Add the new panel group if one was created for the panel
          if (newGroup !== undefined) {
            addPanelGroup(state, newGroup);
          }

          // Open the editor with the new state
          state.panelEditor = editorState;
          // Make sure we are in edit mode
          state.isEditMode = true;
        },
        false,
        '[Panel] openAddPanel'
      );
    },

    editPanelDefinition(panelGroupItemId, panelDefinition) {
      const { panelGroups } = get();

      const panelKey = getPanelKey(panelGroupItemId, panelGroups);
      if (panelKey === undefined) {
        throw new Error(`Could not find Panel Group item ${panelGroupItemId}`);
      }

      set(
        (state) => {
          state.panels[panelKey] = panelDefinition;
        },
        false,
        '[Panel] editPanelDefinition'
      );
    },
  });
}

function getPanelKey(panelGroupItemId: PanelGroupItemId, panelGroups: Record<string, PanelGroupDefinition>) {
  const { panelGroupId, panelGroupItemLayoutId } = panelGroupItemId;
  const panelGroup = panelGroups[panelGroupId];
  if (panelGroup === undefined) {
    throw new Error(`Could not find panel group ${panelGroupId}`);
  }

  const panelKey = getPanelKeyFromPanelGroupDefinition(panelGroup, panelGroupItemLayoutId);

  if (panelKey === undefined) {
    throw new Error(`Could not find panel group item ${panelGroupItemId}`);
  }

  return panelKey;
}

export function getPanelKeyFromPanelGroupDefinition(
  group: PanelGroupDefinition,
  panelGroupLayoutId?: string
): string | undefined {
  if (group.kind === 'Grid') {
    if (!panelGroupLayoutId) {
      throw new Error('Panel group layout id is required');
    }
    const key = group.itemPanelKeys[panelGroupLayoutId];
    if (key === undefined) {
      throw new Error(`Missing panel key of layout ${panelGroupLayoutId}`);
    }
    return key;
  } else {
    return getPanelKeyFromRef(group.content);
  }
}
